import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { FeeRuleDto } from "@/features/fee-engine/api/dto";
import { FeeRuleRowActions } from "@/features/fee-engine/components/fee-rules/fee-rule-row-actions";
import {
  CUSTOMER_SEGMENTS,
  FEE_PURPOSES,
  FEE_RULE_STATUSES,
  type FeeRuleStatus,
} from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (optionnel dans le DTO). */
export type FeeRuleRow = FeeRuleDto & { id: string };

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const STATUS_VARIANTS: Record<
  FeeRuleStatus,
  "warning" | "success" | "secondary"
> = {
  DRAFT: "warning",
  ACTIVE: "success",
  ARCHIVED: "secondary",
};

/**
 * Colonnes de la liste des règles de frais (`POST /FeeRules/all`).
 * Filtres select sur `productCode` (texte libre — filtre custom serveur),
 * `customerSegment`, `feePurpose`, `status`. Identifiants en `NoTranslate`.
 */
export const feeRulesColumns: ColumnsClarFactory<FeeRuleRow> = (navigate) => [
  {
    id: "productCode",
    accessorKey: "productCode",
    header: t("fees.rules.columns.productCode"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "customerSegment",
    accessorKey: "customerSegment",
    header: t("fees.rules.columns.customerSegment"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(CUSTOMER_SEGMENTS) },
  },
  {
    id: "feePurpose",
    accessorKey: "feePurpose",
    header: t("fees.rules.columns.feePurpose"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="outline">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(FEE_PURPOSES) },
  },
  {
    id: "calculationType",
    accessorKey: "calculationType",
    header: t("fees.rules.columns.calculationType"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{getValue() as string}</NoTranslate>
    ),
    // Pas dans FeeRuleFilter.
    meta: { type: "denied" },
  },
  {
    id: "tierBasis",
    accessorKey: "tierBasis",
    header: t("fees.rules.columns.tierBasis"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "payer",
    accessorKey: "payer",
    header: t("fees.rules.columns.payer"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("fees.rules.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as FeeRuleStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "select", options: selectOptions(FEE_RULE_STATUSES) },
  },
  {
    id: "currency",
    accessorKey: "currency",
    header: t("fees.rules.columns.currency"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "effectiveFrom",
    accessorKey: "effectiveFrom",
    header: t("fees.rules.columns.effectiveFrom"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <FeeRuleRowActions feeRule={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
